// メアド収集 状況ダッシュボード（事務所視点）。
// 共通URL（グループLINEに貼る）をコピーでき、登録済/未提出/要確認を一覧する。
// データは GET /api/register/list（本番＝Cloudflare＋マスタ同期が前提）。

import React, { useEffect, useState } from "react";
import { Ic } from "../components/Icons";
import { fetchRegisterList, type RegisterListResult } from "../lib/registerApi";

export function RegisterStatusScreen({ onNav }: { onNav: (s: string) => void }) {
  const [data, setData] = useState<RegisterListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [q, setQ] = useState("");

  const shareUrl = `${window.location.origin}/?register=1`;

  const load = async (manual?: boolean) => {
    if (manual) setRefreshing(true);
    const r = await fetchRegisterList();
    setData(r);
    setLoading(false);
    if (manual) setRefreshing(false);
  };
  useEffect(() => {
    void load();
  }, []);

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      alert("コピーに失敗しました。URLを手動で選択してください。");
    }
  };

  const counts = data?.counts;
  const notSubmitted = (data?.notSubmitted || []).filter(
    (d) => !q.trim() || d.name.includes(q.trim()) || d.branch.includes(q.trim())
  );
  const queue = data?.reviewQueue || [];

  return (
    <div className="content" style={{ maxWidth: 1100 }}>
      <div className="page-title-row">
        <div className="col gap-4">
          <div className="page-title">メアド収集</div>
          <div className="page-sub muted">
            ドライバーがフォームで自己登録 → 確認メールで本人確認 → 正本マスタに反映されます。
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={() => onNav("home")}>
          <Ic.chevronL size={13} /> ホーム
        </button>
        <button className="btn" onClick={() => load(true)} disabled={refreshing}>
          <Ic.history size={14} /> {refreshing ? "更新中..." : "更新"}
        </button>
      </div>

      {/* 共通URL（グループLINEに貼る） */}
      <div className="card" style={{ padding: "18px 22px", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>グループLINEに貼る共通URL</div>
        <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <input className="input" readOnly value={shareUrl} style={{ flex: "1 1 320px", fontFamily: "monospace" }} />
          <button className="btn primary" onClick={copyShare}>
            <Ic.doc size={13} /> {copied ? "✓ コピーしました" : "URLをコピー"}
          </button>
        </div>
        <div className="tiny muted" style={{ marginTop: 8 }}>
          このURL1本を全ドライバーに配ればOKです。各自が氏名とメアドを入力 → 届いた確認メールのボタンを押すと登録完了します。
        </div>
      </div>

      {loading ? (
        <div className="toast info">
          <Ic.info size={14} /> 読み込み中...
        </div>
      ) : !data?.ok ? (
        <div className="toast warn">
          <Ic.alert size={14} />
          <div className="col gap-4" style={{ flex: 1 }}>
            <b>収集状況を取得できませんでした。</b>
            <div className="tiny">{data?.error || "本番（Cloudflare）環境とマスタ同期の設定が必要です。"}</div>
          </div>
        </div>
      ) : (
        <>
          {/* カウント */}
          <div className="ap-statgrid" style={{ marginBottom: 16 }}>
            <Stat label="在籍" value={counts?.active ?? 0} color="#64748b" />
            <Stat label="認証済" value={counts?.verified ?? 0} color="var(--green)" />
            <Stat label="未提出" value={counts?.notSubmitted ?? 0} color="var(--amber)" />
            <Stat label="確認待ち" value={counts?.awaitingClick ?? 0} color="var(--blue)" />
            <Stat label="要確認" value={counts?.reviewQueue ?? 0} color="#cc2f2a" />
          </div>

          {/* 要確認キュー */}
          {queue.length > 0 && (
            <div className="card" style={{ padding: "16px 20px", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "#cc2f2a" }}>
                要確認（{queue.length}件）— 自動で人を特定できなかった登録。本人へ確認のうえマスター編集で登録してください
              </div>
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>入力された氏名</th>
                      <th>営業所</th>
                      <th>メアド</th>
                      <th>理由</th>
                      <th>候補</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queue.map((r) => (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 700 }}>{r.inputName}</td>
                        <td>{r.inputBranch || "—"}</td>
                        <td className="mono tiny">{r.email}</td>
                        <td>{r.reason === "candidate" ? "同姓同名" : "未登録の氏名"}</td>
                        <td className="tiny muted">
                          {r.candidates.map((c) => `${c.name}(${c.branch || "営業所?"})`).join(" / ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="tiny muted" style={{ marginTop: 8 }}>
                ※ なりすまし防止のため、同姓同名・未登録の氏名は自動登録せず、ここに溜めています。
              </div>
            </div>
          )}

          {/* 未提出者 */}
          <div className="filter-bar">
            <div className="search-wrap" style={{ flex: "0 0 320px" }}>
              <Ic.search size={14} />
              <input className="input search" placeholder="氏名 / 営業所で検索" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div style={{ flex: 1 }} />
            <span className="tiny muted num">未提出 {notSubmitted.length} 名</span>
          </div>
          <div className="card list-card">
            <div className="tbl-scroll">
              <table className="tbl list-tbl">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>氏名</th>
                    <th>営業所</th>
                    <th>契約</th>
                  </tr>
                </thead>
                <tbody>
                  {notSubmitted.map((d) => (
                    <tr key={d.id}>
                      <td className="mono muted">{d.id}</td>
                      <td style={{ fontWeight: 700 }}>{d.name}</td>
                      <td>{d.branch || "—"}</td>
                      <td className="tiny">
                        {d.contractTo ? (
                          <span
                            style={{
                              padding: "1px 6px",
                              borderRadius: 4,
                              background: "#2b51c222",
                              color: "#2b51c2",
                              fontWeight: 600,
                            }}
                            title="この日で契約終了予定です。終了日までの支払通知が必要なため収集対象に含めています"
                          >
                            {d.contractTo} 終了予定
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {notSubmitted.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", padding: 20, color: "var(--ink-3)" }}>
                        未提出者はいません 🎉
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="card" style={{ padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: color }} />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink-2)" }}>{label}</span>
      </div>
      <div style={{ fontSize: 27, fontWeight: 800, marginTop: 6 }} className="num">
        {value}
      </div>
    </div>
  );
}
